import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useOnboardingState } from '../../hooks/useOnboardingState.jsx';
import { onboardingV2Api } from '@/data/api/onboardingV2';
import Logo from '@/Asset/images/logo-blaxk.webp';
import BeginnerImage from '@/Asset/images/beginner.webp';
import IntermediateImage from '@/Asset/images/intermediate.webp';
import AdvancedImage from '@/Asset/images/advanced.webp';
import Toast from '@/utils/toastManager';
import TurnsImage from '@/Asset/images/turn.webp';
import JumpsImage from '@/Asset/images/jump.webp';
import FlexibilityImage from '@/Asset/images/flexibility.webp';
import StrengthImage from '@/Asset/images/strength.webp';
import RehabImage from '@/Asset/images/injury.webp';
import EastIcon from '@mui/icons-material/East';
import StepIndicator from '../StepIndicator/StepIndicator';
import './Step2.css';

const Step2 = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { state, updateState } = useOnboardingState();
	const roleFromQuery = useMemo(() => new URLSearchParams(location.search).get('role'), [location.search]);
	const planFromQuery = useMemo(() => new URLSearchParams(location.search).get('plan'), [location.search]);
	const isRetake = useMemo(() => new URLSearchParams(location.search).get('retake') === 'true' || location.state?.isRetake === true, [location.search, location.state?.isRetake]);
	const selectedRole = roleFromQuery || state.onboardingRole || null;
	const isStudioPlan = state.selectedPlanType === 'studio' || planFromQuery === 'studio';
	const isPlusPlan = planFromQuery === 'plus';
	const isDancerParentFlow = !isStudioPlan && selectedRole === 'dancer-parent';
	const isStandardAltFlow = !isStudioPlan && !isDancerParentFlow;
	const totalSteps = isStudioPlan ? (isRetake ? 5 : 6) : 4;
	const stepItems = Array.from({ length: totalSteps }, (_, index) => index + 1);
	const levelOptions = useMemo(
		() => [
			{ id: 'beginner', label: 'Beginner', image: BeginnerImage },
			{ id: 'intermediate', label: 'Intermediate', image: IntermediateImage },
			{ id: 'advanced', label: 'Advanced', image: AdvancedImage }
		],
		[]
	);
	const [selectedLevel, setSelectedLevel] = useState(null);
	const [selectedFocusAreas, setSelectedFocusAreas] = useState([]);
	const [selectedStudentLevels, setSelectedStudentLevels] = useState([]);

	useEffect(() => {
		if (state.currentLevel) setSelectedLevel(state.currentLevel);
		if (state.focusAreas) setSelectedFocusAreas(state.focusAreas);
		if (state.studentLevels) setSelectedStudentLevels(state.studentLevels);
	}, [state.currentLevel, state.focusAreas, state.studentLevels]);

	// Validation function for StepIndicator - checks if current step is valid
	const validateStep = () => {
		if (isDancerParentFlow && !selectedLevel) {
			Toast.error('Please select your present level to continue.');
			return false;
		}
		if (isStandardAltFlow && selectedFocusAreas.length === 0) {
			Toast.error('Please select at least one focus area to continue.');
			return false;
		}
		if (isStudioPlan && selectedStudentLevels.length === 0) {
			Toast.error('Please select at least one student level to continue.');
			return false;
		}
		return true;
	};

	// Handle validation failure for StepIndicator
	const handleValidationFail = () => {
		// Toast is already shown in validateStep
	};
	const focusOptions = useMemo(
		() => [
					{ id: 'turns', label: 'Turns', image: TurnsImage },
					{ id: 'jumps', label: 'Jumps', image: JumpsImage },
					{ id: 'flexibility', label: 'Flexibility', image: FlexibilityImage },
					{ id: 'strength', label: 'Strength', image: StrengthImage },
					{ id: 'injury-reduction-rehab', label: 'Injury Reduction / Rehab', image: RehabImage }
				]
	);
	const studioLevelOptions = useMemo(
		() => [
			{ id: 'beginner-recreational', label: 'Beginner / Recreational', image: BeginnerImage },
			{ id: 'intermediate', label: 'Intermediate', image: IntermediateImage },
			{ id: 'advanced', label: 'Advanced', image: AdvancedImage },
			{ id: 'pre-professional', label: 'Pre-Professional', image: AdvancedImage },
		],
		[]
	);

	useEffect(() => {
		if (roleFromQuery && roleFromQuery !== state.onboardingRole) {
			updateState({ onboardingRole: roleFromQuery });
		}
		if (planFromQuery === 'studio' && state.selectedPlanType !== 'studio') {
			updateState({ selectedPlanType: 'studio', userType: state.userType || 'studio-owner' });
		}
	}, [planFromQuery, roleFromQuery, state.onboardingRole, state.selectedPlanType, state.userType, updateState]);

	const roleLabel = useMemo(() => {
		if (!selectedRole) return '';
		return selectedRole
			.split('-')
			.map(part => part.charAt(0).toUpperCase() + part.slice(1))
			.join(' ');
	}, [selectedRole]);

	const toggleFocusArea = id => {
		setSelectedFocusAreas(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
	};
	const toggleStudioLevel = id => {
		setSelectedStudentLevels(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
	};

	const handleNext = async () => {
		// Validate required selections based on flow
		if (isDancerParentFlow && !selectedLevel) {
			Toast.error('Please select your present level to continue.');
			return;
		}

		if (isStandardAltFlow && selectedFocusAreas.length === 0) {
			Toast.error('Please select at least one focus area to continue.');
			return;
		}

		if (isStudioPlan && selectedStudentLevels.length === 0) {
			Toast.error('Please select at least one student level to continue.');
			return;
		}

		const onboardingPreferences = {
			...(state.onboardingPreferences || {}),
			currentLevel: selectedLevel,
			focusAreas: selectedFocusAreas,
			studentLevels: selectedStudentLevels
		};

		try {
			await onboardingV2Api.updateOnboardingData({
				onboardingStep: 6,
				onboardingPreferences
			});
		} catch (error) {
			Toast.error('Failed to save this step. Please try again.');
			return;
		}

		updateState({
			currentStep: 6,
			currentLevel: selectedLevel,
			focusAreas: selectedFocusAreas,
			studentLevels: selectedStudentLevels,
			onboardingPreferences
		});
		
		const params = new URLSearchParams(location.search);
		if (isRetake && isStandardAltFlow) {
			navigate(`/onboarding/step-4?${params.toString()}`);
			return;
		}

		navigate(`/onboarding/step-3?${params.toString()}`);
	};

	const handleSkip = async () => {
		try {
			await onboardingV2Api.updateOnboardingData({
				onboardingStep: 6
			});
		} catch (error) {
			Toast.error('Failed to save this step. Please try again.');
			return;
		}

		updateState({ currentStep: 6 });
		navigate('/');
	};

	return (
		<div className="onboarding-step-two">
			<div className="onboarding-step-two__grid-overlay" />
			<div className="onboarding-step-two__content">
				<header className="onboarding-step-two__header">
					<div className="onboarding-step-two__brand-row">
						<button
							type="button"
							className="onboarding-step-two__back-btn"
							onClick={() => navigate(`/onboarding/step-1?${new URLSearchParams(location.search).toString()}`)}
						>
							<KeyboardBackspaceOutlinedIcon />
						</button>
						<img src={Logo} alt="The Godfrey Method On Demand" className="onboarding-step-two__logo" />
					</div>

					<StepIndicator 
						totalSteps={totalSteps} 
						currentStep={2}
						validateStep={validateStep}
						onValidationFail={handleValidationFail}
						basePath="/onboarding"
					/>
				</header>

				<main className="onboarding-step-two__main">
					{isDancerParentFlow ? (
						<>
							<h1 className="onboarding-step-two__title">What Is Your Present Level?</h1>

							<div className="onboarding-step-two__cards">
								{levelOptions.map(option => {
									const isSelected = selectedLevel === option.id;
									return (
										<button
											key={option.id}
											type="button"
											className={`onboarding-step-two__card ${isSelected ? 'is-selected' : ''}`}
											onClick={() => setSelectedLevel(option.id)}
										>
											<div className="onboarding-step-two__card-image-wrap">
												<img src={option.image} alt={option.label} className="onboarding-step-two__card-image" />
											</div>
											<div className="onboarding-step-two__card-footer">
												<p>{option.label}</p>
												{isSelected ? <CheckCircleIcon /> : null}
											</div>
										</button>
									);
								})}
							</div>
						</>
					) : isStandardAltFlow ? (
						<>
							<h1 className="onboarding-step-two__title">What Are You Looking To Improve Most?</h1>
							<p className="onboarding-step-two__subtitle">Please select all that apply</p>
							<div className="onboarding-step-two__cards onboarding-step-two__cards--five">
								{focusOptions.map(option => {
									const isSelected = selectedFocusAreas.includes(option.id);
									return (
										<button
											key={option.id}
											type="button"
											className={`onboarding-step-two__card ${isSelected ? 'is-selected' : ''}`}
											onClick={() => toggleFocusArea(option.id)}
										>
											<div className="onboarding-step-two__card-image-wrap">
												<img src={option.image} alt={option.label} className="onboarding-step-two__card-image" />
											</div>
											<div className="onboarding-step-two__card-footer">
												<p>{option.label}</p>
												{isSelected ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
											</div>
										</button>
									);
								})}
							</div>
						</>
					) : isStudioPlan ? (
						<>
							<h1 className="onboarding-step-two__title">What Level You Categorise Your Current Students?</h1>
							<p className="onboarding-step-two__subtitle">Please select all that apply</p>
							<div className="onboarding-step-two__cards onboarding-step-two__cards--four">
								{studioLevelOptions.map(option => {
									const isSelected = selectedStudentLevels.includes(option.id);
									return (
										<button
											key={option.id}
											type="button"
											className={`onboarding-step-two__card is-studio ${isSelected ? 'is-selected' : ''}`}
											onClick={() => toggleStudioLevel(option.id)}
										>
											<p className="onboarding-step-two__studio-card-label">{option.label}</p>
											<span className={`onboarding-step-two__studio-card-dot ${isSelected ? 'is-selected' : ''}`}>
												{isSelected ? <CheckIcon /> : null}
											</span>
										</button>
									);
								})}
							</div>
						</>
					) : (
						<div className="onboarding-step-two__placeholder">
							<h1 className="onboarding-step-two__title">Step 2 For Studio Accounts</h1>
							<p>Custom Step 2 content for studio plans will be added when you share the design.</p>
						</div>
					)}
				</main>

				<footer className="onboarding-step-two__footer">
					<button 
						type="button" 
						onClick={handleNext} 
						className={`onboarding-step-two__continue-btn ${
							(isDancerParentFlow && !selectedLevel) || (isStandardAltFlow && selectedFocusAreas.length === 0) || (isStudioPlan && selectedStudentLevels.length === 0) ? 'is-disabled' : ''
						}`}
						disabled={(isDancerParentFlow && !selectedLevel) || (isStandardAltFlow && selectedFocusAreas.length === 0) || (isStudioPlan && selectedStudentLevels.length === 0)}
					>
						Continue <EastIcon />
					</button>
					<button type="button" onClick={handleSkip} className="onboarding-step-two__skip-btn">
						Skip
					</button>
				</footer>
			</div>
		</div>
	);
};

export default Step2;
